import {useQuery, useMutation, useQueryClient} from "@tanstack/react-query"

import {api, CustomerOffer, CustomerOffersResponse} from "./index"

export const customerOffersKeys = {
	all: ["customerOffers"] as const,
	byCustomer: (customerID: string) => [...customerOffersKeys.all, customerID] as const,
	pendingOffers: (customerID: string) => ["pendingOffers", customerID] as const,
}

const POLL_INTERVAL = 500

type PendingOffer = {
	offerId: string
	expectedStatus: CustomerOffer["Status"]
}

export function useCustomerOffers(customerID: string) {
	const queryClient = useQueryClient()

	const {data: pendingOffers} = useQuery<PendingOffer[]>({
		queryKey: customerOffersKeys.pendingOffers(customerID),
		queryFn: () => [],
		enabled: false,
		initialData: [],
	})

	const query = useQuery<CustomerOffersResponse>({
		queryKey: customerOffersKeys.byCustomer(customerID),
		queryFn: () => api.customer.getOffers(customerID),
		refetchInterval: (q) => {
			const pending = queryClient.getQueryData<PendingOffer[]>(customerOffersKeys.pendingOffers(customerID))
			if (!pending || pending.length === 0) return false

			const data = q.state.data
			if (!data) return POLL_INTERVAL

			const allOffers = [...(data.ActiveOffers || []), ...(data.InactiveOffers || [])]

			const stillPending = pending.filter((p) => {
				const offer = allOffers.find((o) => o.OfferID === p.offerId)
				if (!offer) return true

				if (p.expectedStatus === "ACTIVE") {
					return offer.Status !== "ACTIVE"
				}
				if (p.expectedStatus === "REVOKED") {
					return offer.Status !== "REVOKED"
				}
				return true
			})

			if (stillPending.length !== pending.length) {
				queryClient.setQueryData(customerOffersKeys.pendingOffers(customerID), stillPending)

				if (stillPending.length === 0) {
					return false
				}
			}

			return POLL_INTERVAL
		},
	})

	const hasPendingOffers = pendingOffers && pendingOffers.length > 0

	return {
		...query,
		isRefreshing: hasPendingOffers || query.isRefetching,
	}
}

function parseLocalDate(dateString: string): Date {
	const [year, month, day] = dateString.split("-").map(Number)
	return new Date(year, month - 1, day)
}

function startOfToday(): Date {
	const today = new Date()
	today.setHours(0, 0, 0, 0)
	return today
}

export type OfferStatus = "available" | "upcoming" | "none"

export function useOfferStatus(customerID: string): OfferStatus {
	const {data} = useCustomerOffers(customerID)

	if (!data) return "none"

	const offers = data.ActiveOffers
	if (
		offers.some((o) => {
			if (o.Status.toLowerCase() !== "active") return false
			const today = startOfToday()
			return parseLocalDate(o.StartDate) <= today && today <= parseLocalDate(o.EndDate)
		})
	)
		return "available"
	if (
		offers.some((o) => {
			if (o.Status.toLowerCase() !== "active") return false
			return startOfToday() < parseLocalDate(o.StartDate)
		})
	)
		return "upcoming"
	return "none"
}

type OneOffTier = {
	tierId: string
	tierType: "ONE_OFF"
	discountRate: number
}

type PlanTier = {
	tierId: string
	tierType: "PLAN"
	discountRate: number
	timeFrameWeeks: number
}

type CreateOfferParams = {
	customerID: string
	offerId: string
	startDate: string
	endDate: string
	tiers: Array<OneOffTier | PlanTier>
	agentEmail?: string
	notes?: string
}

type CreateOfferResult = {
	customerID: string
	offerId: string
}

export function useCreateCustomerOffer() {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: async ({customerID, offerId, ...payload}: CreateOfferParams): Promise<CreateOfferResult> => {
			await api.customer.createOffer(customerID, {
				offerId,
				...payload,
			})
			return {customerID, offerId}
		},
		onSuccess: ({customerID, offerId}: CreateOfferResult) => {
			const current = queryClient.getQueryData<PendingOffer[]>(customerOffersKeys.pendingOffers(customerID)) || []
			queryClient.setQueryData<PendingOffer[]>(customerOffersKeys.pendingOffers(customerID), [
				...current,
				{offerId, expectedStatus: "ACTIVE"},
			])
			queryClient.invalidateQueries({queryKey: customerOffersKeys.byCustomer(customerID)})
		},
	})
}

type RevokeOfferParams = {
	customerID: string
	offerID: string
	reason: string
	agentEmail?: string
	notes?: string
}

type RevokeOfferResult = {
	customerID: string
	offerID: string
}

export function useRevokeCustomerOffer() {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: async ({
			customerID,
			offerID,
			reason,
			agentEmail,
			notes,
		}: RevokeOfferParams): Promise<RevokeOfferResult> => {
			await api.customer.revokeOffer(customerID, offerID, {
				Reason: reason,
				AgentEmail: agentEmail,
				Notes: notes,
			})
			return {customerID, offerID}
		},
		onSuccess: ({customerID, offerID}: RevokeOfferResult) => {
			const current = queryClient.getQueryData<PendingOffer[]>(customerOffersKeys.pendingOffers(customerID)) || []
			queryClient.setQueryData<PendingOffer[]>(customerOffersKeys.pendingOffers(customerID), [
				...current,
				{offerId: offerID, expectedStatus: "REVOKED"},
			])
			queryClient.invalidateQueries({queryKey: customerOffersKeys.byCustomer(customerID)})
		},
	})
}
